/**
 * K12Net (K12) SSO helpers — the learner-only single-sign-on entry point.
 *
 * K12 login is a per-environment integration: the client id, base URL and scope come from `REACT_APP_K12_*`
 * env vars, and builds for customers who do not use K12 simply have none of them. Both the login screen (to
 * decide whether to OFFER the entry) and the callback screen (to decide whether it may redirect) need the
 * same answer, so it lives here rather than being asked twice with two slightly different conditions.
 *
 * Pure functions with no React or request dependency, so they are unit-testable — see
 * `K12LoginUtilization.test.ts`.
 */

/** The K12 authorize endpoint, relative to the partner API base URL. */
const K12_AUTHORIZE_PATH = "/GWCore.Web/connect/authorize";

/**
 * Whether this build can run a K12 login at all.
 *
 * All three values are required to form a usable authorize URL. When any is missing the URL degrades to
 * something like `undefined/GWCore.Web/connect/authorize?...&client_id=undefined`, which sends the student
 * to a broken third-party page — so callers use this to refuse the redirect and to keep the button hidden.
 */
export const isK12LoginConfigured = (): boolean =>
  Boolean(
    process.env.REACT_APP_K12_BASE_URL &&
      process.env.REACT_APP_K12_CLIENT_ID &&
      process.env.REACT_APP_K12_SCOPE
  );

/**
 * The K12 authorize URL for one login attempt.
 *
 * `state` is the CSRF binding between the redirect this tab initiated and the callback it later receives;
 * the caller stores it and compares on return.
 *
 * Every parameter is `encodeURIComponent`-ed. The previous inline version ended with
 * `.replace(" ", "%20")`, which — with a string first argument — replaces only the FIRST space in the whole
 * URL. It happened to work solely because the scope is exactly `"openid profile"` (one space); a third scope
 * would have silently produced a malformed URL. The redirect URI needs encoding for the same reason: it
 * carries `://` and a path, and passing it raw relies on the provider being lenient.
 */
export const buildK12AuthorizeUri = (
  redirectUri: string,
  state: string
): string => {
  const params = new URLSearchParams({
    response_type: "code",
    client_id: process.env.REACT_APP_K12_CLIENT_ID ?? "",
    redirect_uri: redirectUri,
    scope: process.env.REACT_APP_K12_SCOPE ?? "",
    state,
  });
  return `${process.env.REACT_APP_K12_BASE_URL}${K12_AUTHORIZE_PATH}?${params.toString()}`;
};
